import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { performOrder } from '@/mocks/perform';

type HandleMethod = 'pickup' | 'delivery' | 'storage';

export default function PerformPage() {
  const navigate = useNavigate();
  const [performWeight, setPerformWeight] = useState(100);
  const [selectedMethod, setSelectedMethod] = useState<HandleMethod>('storage');
  const sliderRef = useRef<HTMLDivElement>(null);

  const order = performOrder;
  const releaseWeight = Math.max(0, order.weight - performWeight);

  // Computations
  const performAmount = performWeight * order.lockPrice;
  const performDepositShare = order.weight > 0 ? order.paidDeposit * (performWeight / order.weight) : 0;
  const performPayable = performAmount - performDepositShare;

  const releaseDiff = (order.currentPrice - order.lockPrice) * releaseWeight;
  const releaseDepositReturn = order.weight > 0 ? order.paidDeposit * (releaseWeight / order.weight) : 0;
  const releaseTotalReturn = releaseDiff + releaseDepositReturn;

  const sliderPercent = order.weight > 0 ? (performWeight / order.weight) * 100 : 0;

  // Slider handlers
  const updateSlider = useCallback((clientX: number) => {
    const el = sliderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(1, x / rect.width));
    const weight = Math.max(0, Math.round(percent * order.weight));
    setPerformWeight(weight);
  }, [order.weight]);

  const handleSliderClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    updateSlider(e.clientX);
  }, [updateSlider]);

  const handleSliderStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => updateSlider(ev.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [updateSlider]);

  const handleConfirm = useCallback(() => {
    if (selectedMethod === 'storage') {
      navigate('/orders/storage');
    } else if (selectedMethod === 'pickup') {
      navigate('/payment', { state: { type: 'perform', amount: performPayable, method: 'pickup' } });
    } else {
      navigate('/payment', { state: { type: 'perform', amount: performPayable, method: 'delivery' } });
    }
  }, [selectedMethod, navigate, performPayable]);

  const methods: { key: HandleMethod; label: string; desc: string; icon: string }[] = [
    { key: 'pickup', label: '到店提货', desc: '前往指定网点提取实物黄金', icon: 'ri-store-2-line' },
    { key: 'delivery', label: '快递发货', desc: '实物黄金快递配送到家', icon: 'ri-truck-line' },
    { key: 'storage', label: '黄金寄存服务', desc: '安全存入金库，随时可提取', icon: 'ri-safe-2-line' },
  ];

  return (
    <div className="min-h-screen bg-brown-50 pb-48">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <h1 className="text-gold-400 text-sm font-bold">申请部分履约</h1>
        <div className="w-8" />
      </div>

      {/* Order Summary */}
      <div className="mx-4 mt-3 bg-white rounded-xl shadow-sm p-4 animate-fade-up">
        <p className="text-gray-400 text-xs mb-3">预约订单信息</p>
        <div className="flex items-center justify-between mb-2">
          <span className="text-brown-600 text-sm">产品</span>
          <span className="text-brown-900 text-sm font-semibold">{order.name}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-brown-600 text-sm">预约重量</span>
          <span className="text-brown-900 text-sm font-semibold">{order.weight}{order.unit}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-brown-600 text-sm">预约锁定价</span>
          <span className="text-brown-900 text-sm font-semibold">¥{order.lockPrice.toFixed(2)}/{order.unit}</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-brown-600 text-sm">当前参考价</span>
          <span className="text-amber-600 text-sm font-semibold">¥{order.currentPrice.toFixed(2)}/{order.unit}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-brown-600 text-sm">已付定金</span>
          <span className="text-brown-900 text-sm font-semibold">
            ¥{order.paidDeposit.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Weight Slider */}
      <div className="mx-4 mt-3 bg-white rounded-xl shadow-sm p-4 animate-fade-up" style={{ animationDelay: '0.1s' }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-brown-900 text-sm font-bold">选择履约数量</h3>
          <span className="text-amber-700 text-sm font-bold">{performWeight}{order.unit}</span>
        </div>
        <p className="text-gray-400 text-xs mb-4">拖动滑块选择本次履约的黄金克数</p>

        <div className="relative px-1 py-2 select-none" ref={sliderRef}>
          <div
            className="absolute -top-1 transition-all duration-150 pointer-events-none"
            style={{ left: `${sliderPercent}%`, transform: 'translateX(-50%)' }}
          >
            <div className="bg-gold-500 text-brown-950 text-xs font-bold px-3 py-1 rounded-lg whitespace-nowrap shadow-sm">
              {performWeight}g
            </div>
            <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-gold-500 mx-auto" />
          </div>
          <div className="relative h-2 bg-gray-200 rounded-full mt-6" onClick={handleSliderClick}>
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-500 to-gold-500 rounded-full pointer-events-none"
              style={{ width: `${sliderPercent}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border-2 border-gold-500 shadow-md cursor-grab active:cursor-grabbing active:scale-110 transition-transform"
              style={{ left: `${sliderPercent}%` }}
              onPointerDown={handleSliderStart}
            />
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-gray-400 text-[11px]">0g</span>
            <span className="text-gray-400 text-[11px]">{order.weight}g</span>
          </div>
        </div>

        {/* Weight Distribution */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-amber-50 rounded-lg p-3">
            <p className="text-amber-700 text-xs mb-1">履约部分</p>
            <p className="text-brown-900 text-lg font-bold">{performWeight}g</p>
          </div>
          <div className="bg-brown-50 rounded-lg p-3">
            <p className="text-brown-600 text-xs mb-1">解除预约</p>
            <p className="text-brown-900 text-lg font-bold">{releaseWeight}g</p>
          </div>
        </div>
      </div>

      {/* Auto Calculation */}
      <div className="mx-4 mt-3 bg-white rounded-xl shadow-sm p-4 animate-fade-up" style={{ animationDelay: '0.15s' }}>
        <p className="text-gray-400 text-xs mb-3">系统自动计算</p>

        {/* Perform Section */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line"></i>
            </div>
            <span className="text-brown-900 text-sm font-bold">履约部分</span>
          </div>
          <div className="pl-7 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">订单金额（预约价×数量）</span>
              <span className="text-brown-900 text-sm font-medium">
                ¥{performAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">扣除对应定金</span>
              <span className="text-brown-900 text-sm font-medium">
                -¥{performDepositShare.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="border-t border-dashed border-gray-200 pt-1.5 flex items-center justify-between">
              <span className="text-brown-900 text-sm font-bold">客户需补缴</span>
              <span className="text-red-500 text-base font-bold">
                ¥{performPayable.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Release Section */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-brown-100 text-brown-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line"></i>
            </div>
            <span className="text-brown-900 text-sm font-bold">解除部分</span>
          </div>
          <div className="pl-7 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">差额收益（{order.currentPrice - order.lockPrice}×{releaseWeight}）</span>
              <span className="text-red-500 text-sm font-medium">
                +¥{releaseDiff.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">对应定金返还</span>
              <span className="text-red-500 text-sm font-medium">
                +¥{releaseDepositReturn.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="border-t border-dashed border-gray-200 pt-1.5 flex items-center justify-between">
              <span className="text-brown-900 text-sm font-bold">进入账户余额</span>
              <span className="text-red-500 text-base font-bold">
                ¥{releaseTotalReturn.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Handle Method Selection */}
      <div className="mx-4 mt-3 bg-white rounded-xl shadow-sm p-4 animate-fade-up" style={{ animationDelay: '0.2s' }}>
        <p className="text-gray-400 text-xs mb-3">选择黄金处理方式</p>
        <div className="space-y-2.5">
          {methods.map((m) => {
            const active = selectedMethod === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setSelectedMethod(m.key)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left cursor-pointer transition-all ${
                  active
                    ? 'border-gold-500 bg-amber-50'
                    : 'border-gray-100 bg-white'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  active ? 'bg-gold-500 text-brown-950' : 'bg-gray-100 text-gray-400'
                }`}>
                  <i className={`${m.icon} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${active ? 'text-brown-900' : 'text-brown-700'}`}>
                      {m.label}
                    </span>
                    {m.key === 'storage' && (
                      <span className="bg-gold-100 text-gold-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                        推荐
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-xs mt-0.5">{m.desc}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  active ? 'border-gold-500 bg-gold-500' : 'border-gray-300'
                }`}>
                  {active && <i className="ri-check-line text-white text-xs"></i>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom Fixed Confirm */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 pt-2 pb-5 z-40">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-500 text-xs">
            {selectedMethod === 'storage' ? '履约后存入金库' : '需补缴金额'}
          </span>
          <span className="text-red-500 text-base font-bold">
            ¥{performPayable.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <button
          onClick={handleConfirm}
          className="w-full py-2.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]"
        >
          确认履约
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
    </div>
  );
}